export const BASE_HERO_HEIGHT = 364;

const MOBILE_MASK_URI =
  "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20375%20210'%20preserveAspectRatio='none'%3E%3Cpath%20d='M0%2C0%20H375%20V194.0%20L375%2C194.0%20L370%2C194.5%20L365%2C194.9%20L360%2C195.4%20L355%2C195.8%20L350%2C196.2%20L345%2C196.6%20L340%2C197.0%20L335%2C197.3%20L330%2C197.7%20L325%2C198.0%20L320%2C198.2%20L315%2C198.5%20L310%2C198.7%20L305%2C198.9%20L300%2C199.1%20L295%2C199.3%20L290%2C199.5%20L285%2C199.6%20L280%2C199.7%20L275%2C199.8%20L270%2C199.9%20L265%2C199.9%20L260%2C200.0%20L255%2C200.0%20L250%2C200.0%20L245%2C200.0%20L240%2C199.9%20L235%2C199.9%20L230%2C199.8%20L225%2C199.7%20L220%2C199.6%20L215%2C199.4%20L210%2C199.3%20L205%2C199.1%20L200%2C198.9%20L195%2C198.7%20L190%2C198.4%20L185%2C198.2%20L180%2C197.9%20L175%2C197.6%20L170%2C197.3%20L165%2C196.9%20L160%2C196.6%20L155%2C196.2%20L150%2C195.7%20L145%2C195.3%20L140%2C194.8%20L135%2C194.4%20L130%2C193.8%20L125%2C193.3%20L120%2C192.7%20L115%2C192.2%20L110%2C191.5%20L105%2C190.9%20L100%2C190.2%20L95%2C189.5%20L90%2C188.8%20L85%2C188.0%20L80%2C187.2%20L75%2C186.3%20L70%2C185.5%20L65%2C184.6%20L60%2C183.6%20L55%2C182.6%20L50%2C181.5%20L45%2C180.4%20L40%2C179.3%20L35%2C178.1%20L30%2C176.8%20L25%2C175.5%20L20%2C174.1%20L15%2C172.6%20L10%2C171.1%20L5%2C169.4%20L0%2C167.6%20Z'%20fill='%23000'/%3E%3C/svg%3E\")";

function buildEllipseMask(
  vw: number,
  vh: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rot: number
): string {
  const svg =
    "%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20" +
    vw +
    '%20' +
    vh +
    "'%20preserveAspectRatio='none'%3E%3Cellipse%20cx='" +
    cx +
    "'%20cy='" +
    cy +
    "'%20rx='" +
    rx +
    "'%20ry='" +
    ry +
    "'%20transform='rotate(" +
    rot +
    '%20' +
    cx +
    '%20' +
    cy +
    ")'%20fill='%23000'/%3E%3C/svg%3E";

  return 'url("data:image/svg+xml,' + svg + '")';
}

function maskStyle(uri: string): React.CSSProperties {
  return {
    maskImage: uri,
    maskRepeat: 'no-repeat',
    maskSize: '100% 100%',
    WebkitMaskImage: uri,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: '100% 100%'
  };
}

export const MOBILE_MASK = maskStyle(MOBILE_MASK_URI);

// The mask canvas is taller than the baseline window (same headroom the desktop ellipse gets)
// so growing the hero reveals more of the same ellipse via CSS centering, rather than gaps
// (fixed-size box re-centering) or stretching (box resizing to match the window). cy is shifted
// down by half the extra canvas height so the baseline crop, once centered, matches today exactly.
export const TABLET_MASK_CANVAS_HEIGHT = 960;
const TABLET_ELLIPSE_CY =
  244.44 + (TABLET_MASK_CANVAS_HEIGHT - BASE_HERO_HEIGHT) / 2;

export const TABLET_MASK = maskStyle(
  buildEllipseMask(
    768,
    TABLET_MASK_CANVAS_HEIGHT,
    777,
    TABLET_ELLIPSE_CY,
    321.3777,
    472.26185,
    48.71
  )
);

// Figma node 7714:19387 — base ellipse radii (half of 642.755 × 944.524)
const EL_RX_BASE = 321.378;
const EL_RY_BASE = 472.262;
const ROT_RAD = 48.69 * (Math.PI / 180);
const COS_ROT = Math.cos(ROT_RAD);
const SIN_ROT = Math.sin(ROT_RAD);
const ELLIPSE_BB = 960; // square bounding box that holds the rotated ellipse

// Ellipse scale: 79% at md (<1280), interpolates to 85% at ≥1440 (per design spec).
// Below 1280 the clip bleeds off the banner right edge; above it anchors to the
// content-area right edge. Uniform scaling keeps the curve geometrically true.
function ellipseScale(heroWidth: number): number {
  if (heroWidth >= 1440) return 0.85;
  if (heroWidth >= 1280) return 0.79 + ((heroWidth - 1280) / 160) * 0.06;

  return 0.79;
}

function ellipseHalfWidth(scale: number): number {
  return Math.sqrt(
    (EL_RX_BASE * scale * COS_ROT) ** 2 + (EL_RY_BASE * scale * SIN_ROT) ** 2
  );
}

function ellipseHalfHeight(scale: number): number {
  return Math.sqrt(
    (EL_RX_BASE * scale * SIN_ROT) ** 2 + (EL_RY_BASE * scale * COS_ROT) ** 2
  );
}

// md (<1280): clip RIGHT edge bleeds 82px past the banner edge (viewport-width-independent).
// xl (≥1280): clip RIGHT edge anchors to the content-area right edge (40px inset from column).
export function computeEllipseLeft(heroWidth: number): number {
  const halfW = ellipseHalfWidth(ellipseScale(heroWidth));
  const cx = heroWidth >= 1280 ? -40 - halfW : 82 - halfW;

  return cx - ELLIPSE_BB / 2;
}

// Figma-baseline top offset for the 960×960 ellipse box at BASE_HERO_HEIGHT.
const DESKTOP_ELLIPSE_BASE_TOP = -201;

// The ellipse's own curve doesn't reach the edges of its 960×960 box — past it there's only
// transparent margin, nothing left to reveal. At the baseline crop that margin is asymmetric
// (much less above the crop than below it), so splitting growth 50/50 exhausts the top margin
// almost immediately and leaves a gap. Instead, split growth in proportion to how much margin
// exists on each side, so both run out at the same time instead of the smaller one running out
// first.
export function computeDesktopEllipseTop(
  heroHeight: number,
  heroWidth: number
): number {
  const delta = heroHeight - BASE_HERO_HEIGHT;
  if (delta <= 0) return DESKTOP_ELLIPSE_BASE_TOP;

  const halfHeight = ellipseHalfHeight(ellipseScale(heroWidth));
  const ellipseTop = ELLIPSE_BB / 2 - halfHeight;
  const ellipseBottom = ELLIPSE_BB / 2 + halfHeight;
  const baselineBoxTop = -DESKTOP_ELLIPSE_BASE_TOP;
  const baselineBoxBottom = baselineBoxTop + BASE_HERO_HEIGHT;
  const topMargin = baselineBoxTop - ellipseTop;
  const bottomMargin = ellipseBottom - baselineBoxBottom;
  const totalMargin = topMargin + bottomMargin;

  const topShift = Math.min(delta, totalMargin) * (topMargin / totalMargin);

  return DESKTOP_ELLIPSE_BASE_TOP + topShift;
}

// SVG mask at 79% of Figma spec — used for md breakpoint (1024–1279px).
export const DESKTOP_ELLIPSE_MASK_MD = buildEllipseMask(
  ELLIPSE_BB,
  ELLIPSE_BB,
  ELLIPSE_BB / 2,
  ELLIPSE_BB / 2,
  EL_RX_BASE * 0.79,
  EL_RY_BASE * 0.79,
  48.69
);

// SVG mask at 85% of Figma spec — used for xl breakpoint (≥1280px).
export const DESKTOP_ELLIPSE_MASK_XL = buildEllipseMask(
  ELLIPSE_BB,
  ELLIPSE_BB,
  ELLIPSE_BB / 2,
  ELLIPSE_BB / 2,
  EL_RX_BASE * 0.85,
  EL_RY_BASE * 0.85,
  48.69
);
